import { z } from "zod/v4";

export const LiteralSchema = z.union([z.string(), z.number(), z.boolean()]);

export type LiteralType = z.infer<typeof LiteralSchema>;

export const JsonSchema: z.ZodType<JsonType> = z.lazy(() =>
  z.union([
    LiteralSchema,
    z.array(JsonSchema),
    z.record(z.union([z.string(), z.number(), z.symbol()]), JsonSchema),
  ])
);

export const castToNumber = z.preprocess(
  (val: unknown) => (typeof val === "string" ? Number(val) : val),
  z.number().or(z.string())
);

export const castToString = z.preprocess(
  (val: unknown) => (typeof val === "number" ? val.toString() : val),
  z.string()
);

export const castToBool = z.preprocess(
  (val: unknown) => (typeof val === "string" ? stringToBoolean(val) : val),
  z.boolean()
);

export const castToNumberOrBoolean = z.preprocess((value: any) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (!isNaN(Number(value))) {
    // Coerce to number if possible
    return Number(value);
  } else if (typeof value === "string") {
    return stringToBoolean(value);
  }

  // If no valid coercion, throw an error
  throw new Error("Invalid value: cannot coerce to number or boolean");
}, z.boolean().or(z.number()));

export const castToCommaList = z.preprocess(
  (arg: unknown) => (Array.isArray(arg) ? arg.join(",") : arg),
  z.string()
);

export const commaListToArray = z.preprocess((arg: unknown) => {
  if (arg === null || arg === undefined) {
    return [];
  }
  return Array.isArray(arg)
    ? arg
    : typeof arg === "string"
      ? arg.split(",")
      : arg;
}, z.string().array());

export type JsonType = LiteralType | { [key: string]: JsonType } | JsonType[];

const stringToBoolean = (value: string): boolean => {
  value = value.toLowerCase();
  if (["y", "yes", "t", "true", "on", "1"].includes(value)) return true;
  if (["n", "no", "f", "false", "off", "0"].includes(value)) return false;
  throw Error(`Invalid truth value ${value}`);
};

export const validateIdNotExponent = z
  .string()
  .refine((val) => !val.toUpperCase().includes("E"), {
    message: "ID cannot be in exponential form",
  });

export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : T[P] extends object
      ? RecursivePartial<T[P]>
      : T[P];
};

export type RecursiveRequired<T> = {
  [K in keyof T]: RecursiveRequired<T[K]>;
} & Required<T>;

export type PathsToProps<T> = T extends string | number | boolean
  ? []
  : {
      [K in Extract<keyof T, string | number | boolean>]: [
        K,
        ...PathsToProps<T[K]>,
      ];
    }[Extract<keyof T, string | number | boolean>];

export type Join<T extends string[], D extends string> = T extends []
  ? never
  : T extends [infer F]
    ? F
    : T extends [infer F, ...infer R]
      ? F extends string
        ? `${F}${D}${Join<Extract<R, string[]>, D>}`
        : never
      : string;

// @ts-ignore
export type NestedKey<T> = Join<PathsToProps<T>, ".">;

export type NestedValue<T, K extends string> = K extends keyof T
  ? T[K]
  : K extends `${infer K0}.${infer KR}`
    ? K0 extends keyof T
      ? NestedValue<T[K0], KR>
      : never
    : never;

export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

export type WithRequired<T, K extends keyof T> = Omit<T, K> & {
  [P in K]-?: NonNullable<T[P]>;
};
export type Nullable<T> = { [K in keyof T]: T[K] | null };

export type ExplicitUndefined<T> = {
  [K in keyof T]: T[K] | undefined;
};

export type CamelToSnakeCase<S extends string> =
  S extends `${infer T}${infer U}`
    ? T extends Capitalize<T>
      ? `_${Lowercase<T>}${CamelToSnakeCase<U>}`
      : `${T}${CamelToSnakeCase<U>}`
    : S;

export type ConvertKeysToSnakeCase<T> = {
  [K in keyof T as CamelToSnakeCase<string & K>]: T[K] extends Record<
    string,
    any
  >
    ? ConvertKeysToSnakeCase<T[K]>
    : T[K];
};

export type Flatten<T> = {
  [K in keyof T]: T[K] extends object ? Flatten<T[K]> : T[K];
}[keyof T];

export type PickAction<T, K> = T extends { action: K } ? T : never;
export type PickType<T, K> = T extends { type: K } ? T : never;
export type PickTypeName<T, K> = T extends { __typename?: K }
  ? T
  : T extends null
    ? never
    : never;
export type FirstParameterType<T extends (...args: any[]) => any> =
  Parameters<T>[0];
